import { useContext, useEffect, useState } from 'react'
import {
  get,
  isNil,
  find,
  isEmpty,
  cloneDeep,
  findIndex,
} from 'es-toolkit/compat'
import Collection from 'ol/Collection'
import { getArea } from 'ol/sphere'

import * as c from 'common/c'
import { getPartner } from 'common/misc'
import { getCropStyle } from 'common/ol/styles'
import { unitConvert } from '@farmtrx/units'
import { subscriptionStatus } from '@farmtrx/options'
import { fromGeoJson, toGeoJson } from 'common/ol/utils'
import { SidePanel, PageContent } from 'components/page'
import ErrorBoundary from 'containers/app/error-boundary'
import MapFieldsPanel from 'containers/map-fields-panel'
import {
  ArcGISMaps,
  BingMaps,
  FarmtrxMap,
  FocusField,
  //ViewInfo, // uncomment when needed to see the zoom level and center coordinates
} from 'components/map'
import FeatureFlag from 'components/feature-flag'
import { FEATURE_FLAGS } from 'feature-flags'
import FeatureFlagsContext from 'context/feature-flags-context'
import EditBoundaryHelpBubble from 'components/map/edit-boundary-help-bubble'
import ElevationProfileHelpBubble from 'components/map/elevation-profile-help-bubble'
import FieldInfo from 'components/map/field-info'
import AreaInfoView from 'components/map/area-info-view'
import MapSourceBoundaryProvider from 'components/map/map-source-boundary-provider'
import MapWMSLayer from 'components/map/wms-layer'
import MapStaticBoundaryLayer from 'components/map/static-boundary-layer'
import MapOutsideBoundaryPointsLayer from 'components/map/outside-boundary-points-layer'
import MapBoundaryLayer from 'components/map/boundary-layer'
import MapFieldsOverview from 'components/map/fields-overview'
import MapProvider from 'components/map/map-provider'
import BoundaryTool from 'components/map/boundary-tool'
import HarvestToolbar from 'components/harvest/harvest-toolbar'
import MapNotesLayer from 'components/map/notes-layer'
import LayerLegend from 'components/layer-legend'
import AccountDisabledPage from 'components/account-disabled'
import ModifyInteraction from 'components/map/modify-interaction'
import DrawInteraction from 'components/map/draw-interaction'
import DrawBoxInteraction from 'components/map/draw-box-interaction'
import DrawLineInteraction from 'components/map/draw-line-interaction'
import ElevationLineLayer from 'components/map/elevation-line-layer'
import ElevationProfileView from 'components/map/elevation-profile-view'
import PointInfo from 'components/map/point-info'
import SwathInfo from 'components/map/swath-info'
import HoverInfo from 'components/map/hover-info'
import AddNote from 'components/add-note'
import SwitchSplit from 'components/switch-split'
import OnMapHover from 'components/map/field-hover'
import MarkerLayer from 'components/map/marker-layer'
import ThreeDMap from 'components/map/three-d'
import ThreeDControl from 'components/map/three-d-control'
import LayersControl from 'components/map/layers-control'
import useMap from 'hooks/use-map'
import { TEST_IDS } from 'test-ids'

import {
  removeFeatures,
  fetchFeatures,
  saveFeatures,
  editFeatures,
  getLayerInfoByHarvest,
} from 'api/field'
import { getHarvestFeatures } from 'api/harvest'
import { showPopup, showModal } from 'actions/app'
import { fieldFetched } from 'actions/field'
import {
  stopEditField,
  areaInfoReq,
  elevationProfileReq,
  switchDrawType,
  stopDrawingBoundary,
  startEditBoundary,
  stopEditBoundary,
  boundarySaved,
  pointInfoClr,
  swathInfoClr,
  elevationProfileClr,
  setThreeDEnabled,
} from 'actions/map'

export default function MapPanel() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const featureFlags = useContext(FeatureFlagsContext)
  const maxFieldSizeSqm = featureFlags[FEATURE_FLAGS.largeFieldBoundaries]
    ?.isEnabled
    ? c.LARGE_FIELD_MAX_SIZE_SQM
    : c.MAX_FIELD_SIZE_SQM
  const [showEditBoundaryHelp, setShowEditBoundaryHelp] = useState(true)
  const [showElevationProfileHelp, setShowElevationProfileHelp] =
    useState(false)

  const cffEnabled = useSelector((state) =>
    get(state, 'account.singleton.prefs.cff.enabled', false),
  )
  const client = useSelector((state) => get(state, `client.singleton`))
  const farms = useSelector((state) => get(state, `farm.selectedFarms`))

  const tenant = useSelector((state) => state.account.singleton)
  const userPrefs = useSelector((state) => state.account.singleton.prefs)
  const year = useSelector((state) => state.field.year)
  const field = useSelector((state) => get(state, 'field.singleton'))
  const fieldEdit = useSelector((state) => get(state, 'map.editField', false))
  const drawBoundary = useSelector((state) => get(state, 'map.drawBoundary'))
  const editBoundary = useSelector((state) => get(state, 'map.editBoundary'))
  const drawHole = useSelector((state) => get(state, 'map.drawHole'))
  const drawType = useSelector((state) => get(state, 'map.drawType'))
  const fieldFeatures = useSelector((state) => get(state, 'field.features'))
  const defaultExtent = useSelector((state) =>
    get(state, 'field.defaultExtent'),
  )
  const layers = useSelector((state) => get(state, 'field.layers'))
  const harvest = useSelector((state) => state.harvest.current)
  const harvests = useSelector((state) => state.harvest.collection)
  const harvestFeaturesArr = useSelector((state) => state.harvest.features)
  const legendUrls = useSelector((state) => get(state, 'map.legendUrl'))
  const wmsLayers = useSelector((state) => get(state, 'map.wmsLayers'))
  const stateSwathParameters = useSelector((state) => state.map.swathParameters)

  const featuresOverviewArr = useSelector((state) =>
    get(state, 'field.featuresOverview', []),
  )
  const layerInfo = useSelector((state) => get(state, 'field.layerInfo'))
  const threeD = useSelector((state) => get(state, 'map.threeDEnabled', false))

  const [selectedFeatures, setSelectedFeatures] = useState([])
  // const [unselectedFeatures, setUnselectedFeatures] = useState([])

  const [showPointInfo, setShowPointInfo] = useState(false)
  const [showSwathInfo, setShowSwathInfo] = useState(false)
  const [showHoverInfo, setShowHoverInfo] = useState(false)
  const [showAreaInfo, setShowAreaInfo] = useState(false)
  const [showElevationProfile, setShowElevationProfile] = useState(false)
  const [showAddNote, setShowAddNote] = useState(false)
  const fields = useSelector((state) => get(state, 'field.collection', []))
  const [featureCollection, setFeatureCollection] = useState(undefined)
  const [originalFeature, setOriginalFeature] = useState(undefined)
  const [filteredWMSLayers, setFilteredWMSLayers] = useState([])
  const [filteredLegends, setFilteredLegends] = useState([])
  const [selectedSwathParameter, setSelectedSwathParameter] = useState('yield')

  const unsavedChanges = useSelector((state) =>
    get(state, 'map.unsavedBoundary'),
  )
  const map = useMap()

  const ref = useRef()
  const subscriptions = useSelector((state) => state.account.subscriptions)
  const permissions = useSelector((state) => state.account.permissions)
  const appState = useSelector((state) => get(state, 'app.state'))

  useEffect(() => {
    let swathColumn = stateSwathParameters.find(
      (swath) => swath.harvestId === harvestId,
    )

    let swathParameter = swathColumn?.swathParameter || 'yield'
    setSelectedSwathParameter(swathParameter)
  }, [stateSwathParameters])

  useEffect(() => {
    if (cffEnabled && (farms?.length || client) && field) {
      let filteredLayers = []
      let filteredUrls = []
      let selectedFarms = []
      if (farms?.length) {
        selectedFarms = farms
      }
      if (selectedFarms?.length && harvests?.length) {
        harvests.map((h) => {
          const fieldIndex = findIndex(fields, (field) => {
            return field.id === h.fieldId
          })
          const farmIndex = findIndex(selectedFarms, (farm) => {
            return farm.id === field.farmId
          })
          if (fieldIndex > -1 && farmIndex > -1) {
            const layer = wmsLayers.filter((l) => {
              return l.harvestId === h.id
            })
            const url = legendUrls.filter((u) => {
              return u.harvestId === harvest?.id
            })
            if (layer?.length) {
              filteredLayers = filteredLayers.concat(layer)
            }
            if (url?.length) {
              filteredUrls = filteredUrls.concat(url)
            }
          }
        })
        setFilteredWMSLayers(filteredLayers)
        setFilteredLegends(filteredUrls)
      } else if (wmsLayers.length > 0 && harvests?.length) {
        setFilteredWMSLayers(wmsLayers)
        setFilteredLegends(legendUrls)
      } else {
        setFilteredWMSLayers([])
        setFilteredLegends([])
      }
    } else if (wmsLayers.length > 0 && harvests?.length) {
      setFilteredWMSLayers(wmsLayers)
      setFilteredLegends(legendUrls)
    } else {
      setFilteredWMSLayers([])
      setFilteredLegends([])
    }
  }, [
    harvests,
    fields,
    cffEnabled,
    client,
    farms,
    wmsLayers,
    legendUrls,
    harvest,
  ])

  useEffect(() => {
    if (
      subscriptions &&
      subscriptions.length &&
      permissions.includes('farm:write')
    ) {
      let subscriptionPeriods = subscriptions[0]['subscriptionPeriods']
      if (!subscriptionPeriods?.length) return
      let maxDate = new Date(
        Math.max(...subscriptionPeriods.map((e) => new Date(e.startDate))),
      )
        .toISOString()
        .split('T')[0]
      let sp = subscriptionPeriods.find(
        (s) =>
          s.startDate === maxDate &&
          (s.subscriptionStatus === subscriptionStatus.PaymentDue ||
            s.subscriptionStatus === subscriptionStatus.PaymentDueFinal),
      )
      if (sp) {
        const notificationKey = `${c.SUBSCRIPTIONS_POPUP_NOTIFICATION}_${sp.id}`
        if (localStorage.getItem(notificationKey) === null) {
          dispatch(
            showModal(
              <div>
                {t('subscription_renewal_notification')},
                <a href="/admin/subscription"> {t('here')}</a>
              </div>,
              {
                title: t('subscription_renewal_notification_header'),
              },
            ),
          )
          localStorage.setItem(notificationKey, JSON.stringify(false))
        }
      }
    }
  }, [subscriptions, permissions])

  useEffect(() => {
    if (localStorage.getItem(c.ACCOUNT_DISABLED_POPUP_NOTIFICATION) === null) {
      if (appState === c.APP_STATE.ACCOUNT_DISABLED) {
        dispatch(
          showModal(<AccountDisabledPage partner={getPartner()} />, {
            title: t('account_deactivated_title'),
          }),
        )
        localStorage.setItem(
          c.ACCOUNT_DISABLED_POPUP_NOTIFICATION,
          JSON.stringify(false),
        )
      }
    }
  })

  useEffect(() => {
    if (harvest) {
      dispatch(getLayerInfoByHarvest(harvest.id, harvest.crop_year))
    }
  }, [harvest])

  // on field select
  useEffect(() => {
    if (field && field.id) {
      setFeatureCollection(new Collection([]))
      dispatch(fetchFeatures(field.id, year))
    }
  }, [field, year])

  // parse fetched features
  useEffect(() => {
    //setModifySelectedFeatures(false)
    setSelectedFeatures([])

    const featuresArr = []

    if (fieldFeatures && fieldFeatures !== c.UNDEFINED && !unsavedChanges) {
      fieldFeatures.forEach((item) => {
        const id = item?.fieldId || item.id
        if (id === field?.id) {
          featuresArr.push(
            Object.assign({}, { type: 'Feature' }, { ...item }, { id: id }),
          )
        }
      })
    }
    let newFeature = fromGeoJson({
      type: 'FeatureCollection',
      features: featuresArr,
    })

    if (featuresArr.length > 0 || fieldFeatures === undefined) {
      if (cffEnabled) {
        let filteredFeatures = new Collection([])
        let selectedFarms = []
        if (farms?.length) {
          selectedFarms = farms
        }
        if (selectedFarms?.length && featuresArr?.length) {
          featuresArr.map((feature) => {
            const fieldIndex = findIndex(fields, (field) => {
              return field.id === feature.fieldId
            })
            const farmIndex = findIndex(selectedFarms, (farm) => {
              return farm.id === field.farmId
            })
            if (fieldIndex > -1 && farmIndex > -1) {
              newFeature.forEach((f) => {
                if (f.getId() == feature.id) {
                  filteredFeatures.push(f)
                }
              })
            }
          })
          setFeatureCollection(filteredFeatures)
        }
      } else {
        setFeatureCollection(newFeature)
      }
    }
  }, [
    fieldFeatures,
    field,
    editBoundary,
    unsavedChanges,
    farms,
    client,
    cffEnabled,
  ])

  // features overview
  const featuresOverview = useMemo(() => {
    const allFeaturesArr = []

    if (featuresOverviewArr && featuresOverviewArr !== undefined) {
      featuresOverviewArr.forEach((item) => {
        allFeaturesArr.push(Object.assign({}, { type: 'Feature' }, { ...item }))
      })
    }
    return fromGeoJson({
      type: 'FeatureCollection',
      features: allFeaturesArr,
    })
  }, [featuresOverviewArr])

  const harvestFeatures = useMemo(() => {
    const allFeaturesArr = []
    if (harvestFeaturesArr && harvestFeaturesArr.size !== 0) {
      const arr = Array.from(harvestFeaturesArr.values())
      if (arr && harvestFeaturesArr !== c.UNDEFINED) {
        arr.forEach((item) => {
          allFeaturesArr.push(
            Object.assign({}, { type: 'Feature' }, { ...item }),
          )
        })
      }
    }
    return fromGeoJson({
      type: 'FeatureCollection',
      features: allFeaturesArr,
    })
  }, [harvestFeaturesArr])

  const onBoundarySelect = useCallback(
    (selected) => {
      if (showPointInfo || showAreaInfo || showSwathInfo || showHoverInfo)
        return
      const selectedField = selected[0]
      const id = selectedField?.getId()
      if (id) {
        const field = find(fields, (o) => o.id === id)
        if (field) {
          dispatch(fieldFetched(field))
          dispatch(fetchFeatures(id, year))
        }
      }
      setSelectedFeatures(selected)
      // setUnselectedFeatures(unselected)
      selected[0]?.setStyle(cropStyleFunction)
    },
    [
      fields,
      showPointInfo,
      showAreaInfo,
      showSwathInfo,
      showHoverInfo,
      year,
      featuresOverviewArr,
    ],
  )

  // eslint-disable-next-line no-unused-vars
  function cropStyleFunction(feature, resolution) {
    if (feature) {
      const cropId = featuresOverviewArr.find(
        (f) => f.id === feature.id_,
      )?.cropId
      return getCropStyle(cropId, undefined)['select']
    }
    return getCropStyle(undefined, undefined)['select']
  }

  // const selectedFeatureCollection = useMemo(() => {
  //   return new Collection(selectedFeatures)
  // }, [selectedFeatures])

  const harvestSelected = useMemo(() => {
    return !isNil(harvest)
  }, [harvest])

  useEffect(() => {
    if (harvest && harvest.id) {
      dispatch(getHarvestFeatures(harvest.id))
    }
  }, [harvest, showAddNote])

  // Field Boundary
  const boundary = useMemo(() => {
    let _boundary
    if (!isEmpty(featureCollection)) {
      const fArr = featureCollection?.getArray()
      for (let i = 0; i !== fArr.length; i++) {
        const f = fArr[i]
        if (
          f.get(c.OL_PROP_TYPE) === c.FTYPE_BOUNDARY ||
          isNil(f.get(c.OL_PROP_TYPE))
        ) {
          _boundary = f
          // const styleName = _boundary.get('style')
          // if (!isNil(styleName)) {
          //   const selectStyleToApply = get(colorList, `${styleName}.select`)
          //   _boundary.setStyle(editBoundary ? modifyStyles : selectStyleToApply)
          // } else {
          //   _boundary.setStyle(editBoundary ? modifyStyles : defaultStyle)
          // }
          break
        }
      }
    }

    return _boundary
  }, [featureCollection])

  const switchToDrawTypePolygon = useCallback(() => {
    const _originalFeature = cloneDeep(featureCollection)

    if (_originalFeature != undefined) {
      setOriginalFeature(_originalFeature)
    }
    dispatch(switchDrawType('MultiPolygon'))
  })

  const switchToDrawTypeCircle = useCallback(() => {
    const _originalFeature = cloneDeep(featureCollection)

    if (_originalFeature != undefined) {
      setOriginalFeature(_originalFeature)
    }
    dispatch(switchDrawType('Circle'))
  })

  const removeBoundary = useCallback(() => {
    dispatch(removeFeatures(field.id))
  }, [field])

  const modifyBoundary = useCallback(() => {
    const _originalFeature = cloneDeep(featureCollection)
    if (isEmpty(selectedFeatures)) {
      setSelectedFeatures(featureCollection)
    }
    if (_originalFeature != undefined) {
      if (!unsavedChanges) {
        setOriginalFeature(_originalFeature)
      }
      dispatch(startEditBoundary())
    }
  }, [selectedFeatures, featureCollection, unsavedChanges])

  const fieldSelected = useMemo(() => {
    return !isNil(field)
  }, [field, featuresOverview])

  const doneModifyBoundary = useCallback(() => {
    let area = 0
    featureCollection.forEach(function (feature) {
      area += getArea(feature.getGeometry())
    })
    if (featureCollection.getLength() === 0) {
      return
    }
    if (area <= maxFieldSizeSqm) {
      if (harvests.length > 0) {
        const currentYear = new Date().getFullYear()
        const defaultYear = year === currentYear ? null : year
        const harvestYear = harvest?.cropYear ? harvest.cropYear : defaultYear
        const harvestId = harvest?.id ? harvest.id : harvests[0].id
        dispatch(
          editFeatures(
            field.id,
            toGeoJson(featureCollection),
            harvestId,
            harvestYear,
          ),
        )
      } else {
        if (originalFeature != undefined) {
          dispatch(
            editFeatures(
              field.id,
              toGeoJson(featureCollection),
              undefined,
              year,
            ),
          )
        } else if (featureCollection != undefined) {
          dispatch(saveFeatures(field.id, toGeoJson(featureCollection)))
        }
      }

      dispatch(boundarySaved())
      dispatch(stopEditField())
      setOriginalFeature(undefined)
    } else {
      const convertedArea = Math.floor(
        unitConvert.convertAreaToPref(area, userPrefs['units']['area_in']),
      )
      const maxArea = Math.floor(
        unitConvert.convertAreaToPref(
          maxFieldSizeSqm,
          userPrefs['units']['area_in'],
        ),
      )
      dispatch(
        showModal(
          <div>
            {t('field_edit_error', { field_name: field.name })}
            <br />
            {'- '}
            {t('field_too_large', {
              max_field_area: maxArea,
              field_area: convertedArea,
              units: userPrefs['units']['area_in'],
            })}
          </div>,
          {
            title: t('field_boundary_error'),
          },
        ),
      )
    }
  }, [
    field,
    featureCollection,
    harvests,
    harvest,
    year,
    originalFeature,
    maxFieldSizeSqm,
  ])

  const cancelModifyBoundary = useCallback(() => {
    if (unsavedChanges && originalFeature) {
      const originalFeaturesArray = originalFeature.getArray()
      featureCollection.forEach((feature) => {
        const id = feature.getId()
        for (let i = 0; i < originalFeaturesArray.length; i++) {
          const oId = originalFeaturesArray[i].getId()
          if (oId == id) {
            feature.setGeometry(originalFeaturesArray[i].getGeometry())
          }
        }
      })

      setFeatureCollection(originalFeature)
    }
    setOriginalFeature(undefined)
    dispatch(stopEditBoundary())
    dispatch(stopEditField())

    dispatch(boundarySaved())
  }, [field, featureCollection, map, originalFeature, unsavedChanges])

  const onBoundaryDraw = useCallback(
    (feature) => {
      featureCollection.forEach((item) => {
        const id = item.getId()
        if (id == field.id) {
          item.setGeometry(feature.getGeometry())
        }
      })
      if (!feature.getId()) {
        feature.setId(field.id)
      }

      feature.set(c.OL_PROP_TYPE, c.FTYPE_BOUNDARY)

      // TODO: temporarily to impose 1 polygon limit. When removed use saveBoundary() instead
      if (field.id && feature) {
        let _newFeature = feature
        if (!(feature instanceof Collection)) {
          _newFeature = new Collection([feature])
        }
        dispatch(stopDrawingBoundary())
        setFeatureCollection(_newFeature)
      }
    },
    [field, featureCollection],
  )

  const onHoleDraw = useCallback(
    (feature) => {
      featureCollection.forEach((item) => {
        const id = item.getId()
        if (id == field.id) {
          item.setGeometry(feature.getGeometry())
        }
      })
      if (!feature.getId()) {
        feature.setId(field.id)
      }

      feature.set(c.OL_PROP_TYPE, c.FTYPE_BOUNDARY)

      // TODO: temporarily to impose 1 polygon limit. When removed use saveBoundary() instead
      if (field.id && feature) {
        let _newFeature = feature
        if (!(feature instanceof Collection)) {
          _newFeature = new Collection([feature])
        }
        setFeatureCollection(_newFeature)
      }
    },
    [featureCollection, drawHole],
  )

  const isEditing = useMemo(
    () => drawBoundary || drawHole || editBoundary,
    [drawBoundary, drawHole, editBoundary],
  )

  // Snapshot for the cancel/restore path. This used to run inside the isEditing
  // memo, i.e. during render; the trigger conditions are unchanged.
  useEffect(() => {
    const _originalFeature = cloneDeep(featureCollection)

    if (_originalFeature != undefined) {
      setOriginalFeature(_originalFeature)
    }
  }, [drawBoundary, drawHole, editBoundary, featureCollection, unsavedChanges])

  const showOverview = useMemo(() => {
    return featureCollection?.getLength() === 0 && isNil(field)
  }, [featureCollection, field])

  const focusActive = useMemo(() => {
    return !isNil(field) || showOverview
  }, [field, showOverview])

  const onPointClick = useCallback((enable = true) => {
    setShowAddNote(false)
    setShowAreaInfo(false)
    setShowSwathInfo(false)
    setShowHoverInfo(false)
    setShowElevationProfile(false)
    dispatch(pointInfoClr())
    dispatch(swathInfoClr())
    dispatch(elevationProfileClr())

    setShowPointInfo(enable)
    // setShowOverrideCropType(false)
  }, [])

  const onSwathInfo = useCallback((enable = true) => {
    setShowAddNote(false)
    setShowPointInfo(false)
    setShowAreaInfo(false)
    setShowHoverInfo(false)
    setShowElevationProfile(false)
    dispatch(pointInfoClr())
    dispatch(swathInfoClr())
    dispatch(elevationProfileClr())

    setShowSwathInfo(enable)
  }, [])

  const onHoverInfo = useCallback((enable = true) => {
    setShowAddNote(false)
    setShowPointInfo(false)
    setShowSwathInfo(false)
    setShowAreaInfo(false)
    setShowElevationProfile(false)
    dispatch(pointInfoClr())
    dispatch(swathInfoClr())
    dispatch(elevationProfileClr())

    setShowHoverInfo(enable)
  }, [])

  const onAreaClick = useCallback(() => {
    setShowAddNote(false)
    setShowPointInfo(false)
    setShowSwathInfo(false)
    setShowHoverInfo(false)
    setShowElevationProfile(false)
    setShowAreaInfo(true)
    dispatch(swathInfoClr())
    dispatch(elevationProfileClr())
    // setShowOverrideCropType(false)
  }, [])

  const onElevationProfileClick = useCallback((enable = true) => {
    setShowAddNote(false)
    setShowPointInfo(false)
    setShowSwathInfo(false)
    setShowHoverInfo(false)
    setShowAreaInfo(false)
    setShowElevationProfile(enable)
    setShowElevationProfileHelp(enable)
    dispatch(swathInfoClr())
    if (!enable) {
      dispatch(elevationProfileClr())
    }
  }, [])

  // Leaving the field behind leaves 3D with nothing to show and no way back:
  // the control is hidden at overview zoom, and 3D keeps the Select
  // interaction off, so a field could no longer be picked from the map.
  useEffect(() => {
    if (threeD && !fieldSelected) dispatch(setThreeDEnabled(false))
  }, [threeD, fieldSelected])

  // The 2D drawing and inspection tools cannot work over the globe: ol-cesium
  // clears the map's interactions in 3D, so their canvas-space state would sit
  // there stale. PointInfo in particular renders an ol/Overlay with the default
  // stopEvent, which lives outside the container ol-cesium hides, so it would
  // float over the globe at a pixel that no longer means anything.
  useEffect(() => {
    if (!threeD) return
    setShowPointInfo(false)
    setShowSwathInfo(false)
    setShowHoverInfo(false)
    setShowAreaInfo(false)
    setShowElevationProfile(false)
    setShowElevationProfileHelp(false)
    setShowAddNote(false)
    dispatch(pointInfoClr())
    dispatch(swathInfoClr())
    dispatch(elevationProfileClr())
    dispatch(stopEditBoundary())
    dispatch(stopDrawingBoundary())
    dispatch(stopEditField())
  }, [threeD])

  const onAddNote = useCallback(() => {
    setShowPointInfo(false)
    setShowAreaInfo(false)
    setShowSwathInfo(false)
    setShowHoverInfo(false)
    setShowElevationProfile(false)

    setShowAddNote(!showAddNote)
    dispatch(swathInfoClr())
    dispatch(elevationProfileClr())
    // setShowOverrideCropType(false)
  }, [showAddNote])

  const onDrawBox = useCallback(
    (extent, pixel) => {
      dispatch(areaInfoReq(extent))
      dispatch(
        showPopup(<AreaInfoView />, {
          title: 'Area Info',
          pixel,
        }),
      )
      setShowAreaInfo(false)
      dispatch(swathInfoClr())
    },
    [showAreaInfo],
  )

  const onDrawLine = useCallback((geometry) => {
    setShowElevationProfile(false)
    setShowElevationProfileHelp(false)
    dispatch(elevationProfileReq(geometry))
  }, [])

  const harvestId = harvest?.id
  filteredWMSLayers.sort((a, b) => a.zindex - b.zindex)
  return (
    <MapProvider
      onSelect={onBoundarySelect}
      selectActive={!threeD}
      editing={isEditing || threeD}
    >
      <SidePanel zindex={2}>
        <MapFieldsPanel />
      </SidePanel>
      <PageContent zindex={1}>
        {editBoundary && showEditBoundaryHelp && (
          <EditBoundaryHelpBubble
            onClose={() => setShowEditBoundaryHelp(false)}
          />
        )}
        {showElevationProfile && showElevationProfileHelp && (
          <ElevationProfileHelpBubble
            onClose={() => setShowElevationProfileHelp(false)}
          />
        )}
        {/* uncomment when needed to see the zoom level and center coordinates */}
        {/* <ViewInfo /> */}
        <ErrorBoundary trans={t}>
          <OnMapHover />
          <MapSourceBoundaryProvider features={featuresOverview}>
            {/* Esri is the default, so a build still carrying the retired
                MAP_PROVIDER=google falls through to it rather than to Bing. */}
            {MAP_PROVIDER === 'bing' ? <BingMaps /> : <ArcGISMaps />}
            {filteredWMSLayers.length === 0 ? (
              <MapStaticBoundaryLayer
                fieldLayerName={STATIC_BOUNDARY_LAYER_NAME}
                tenantId={tenant.id}
                mapRef="map"
                layerZIndex={3}
              />
            ) : null}
            <FeatureFlag featureName={FEATURE_FLAGS.outsideBoundaryPoints}>
              <MapOutsideBoundaryPointsLayer
                tenantId={tenant.id}
                mapRef="map"
                layerZIndex={5}
              />
            </FeatureFlag>
            {!editBoundary &&
            filteredWMSLayers.length > 0 ? null : (harvestId &&
                layers[harvestId]?.boundary.selected) ||
              editBoundary ? (
              <MapBoundaryLayer
                features={featureCollection}
                modify={editBoundary}
              />
            ) : null}
            {editBoundary && !threeD ? (
              <ModifyInteraction features={featureCollection} />
            ) : null}
            {onBoundaryDraw && !threeD ? (
              <DrawInteraction
                withMemory={false}
                onDraw={onBoundaryDraw}
                active={drawBoundary}
                type={drawType}
              />
            ) : null}
            {onHoleDraw && !threeD ? (
              <DrawInteraction
                withMemory={false}
                onDraw={onHoleDraw}
                active={drawHole}
                type={drawType}
              />
            ) : null}
            <FocusField
              boundary={boundary}
              defaultExtent={defaultExtent}
              active={focusActive}
            />
            {filteredWMSLayers.length > 0
              ? filteredWMSLayers.map((layerData) => (
                  <MapWMSLayer
                    key={`${layerData.name}_${layerData.harvestId}`}
                    layersName={layerData.name}
                    bbox={layerData.bbox}
                    legendUrl={layerData.legendUrl}
                    harvestId={layerData.harvestId}
                    tenantId={tenant.id}
                    mapRef="map"
                    layerZIndex={layerData.zindex}
                    wmsLayer={layerData}
                    swathParameters={stateSwathParameters}
                  />
                ))
              : null}
            <FarmtrxMap ref={ref} />
            <FeatureFlag featureName={FEATURE_FLAGS.threeDMap}>
              <ThreeDMap />
            </FeatureFlag>
            <MapFieldsOverview features={featuresOverview} />
            {showPointInfo && !threeD ? <PointInfo /> : null}
            {showSwathInfo && !threeD ? <SwathInfo /> : null}
            {showHoverInfo && !threeD ? <HoverInfo /> : null}
            {showAreaInfo && !threeD ? (
              <DrawBoxInteraction onDrawBox={onDrawBox} />
            ) : null}
            {showElevationProfile && !threeD ? (
              <DrawLineInteraction onDrawLine={onDrawLine} />
            ) : null}
            <ElevationLineLayer />
            <ElevationProfileView />
            {showAddNote && !threeD ? <AddNote /> : null}
            {showAddNote ? (
              <MapNotesLayer featureCollection={harvestFeatures} />
            ) : null}
            <FeatureFlag featureName={FEATURE_FLAGS.operators}>
              <MarkerLayer />
            </FeatureFlag>
            <>
              {(fieldEdit || isEditing) && !threeD ? (
                <BoundaryTool
                  onEdit={modifyBoundary}
                  onDelete={removeBoundary}
                  onDrawPolygon={switchToDrawTypePolygon}
                  onDrawCircle={switchToDrawTypeCircle}
                  onDone={doneModifyBoundary}
                  onCancel={cancelModifyBoundary}
                  drawTypeSelected={drawType}
                  features={featureCollection}
                />
              ) : fieldSelected &&
                harvestSelected &&
                filteredWMSLayers &&
                filteredWMSLayers.length ? (
                <HarvestToolbar
                  pointInfo={showPointInfo}
                  onPointInfo={onPointClick}
                  swathInfo={showSwathInfo}
                  onSwathInfo={onSwathInfo}
                  hoverInfo={showHoverInfo}
                  onHoverInfo={onHoverInfo}
                  elevationProfile={showElevationProfile}
                  onElevationProfile={onElevationProfileClick}
                  areaInfo={showAreaInfo}
                  onAreaInfo={onAreaClick}
                  addNote={showAddNote}
                  onAddNote={onAddNote}
                  disabled={threeD}
                />
              ) : null}
              {fieldSelected ? (
                <FieldInfo field={field} harvest={harvest} />
              ) : null}
              <LayersControl />
              {/* Only offered once a field is in view. Terrain is meaningless
                  at overview zoom, the DEM is per-harvest, and 3D disables the
                  Select interaction that picking a field from the map depends
                  on — so offering it here would be a dead end. */}
              {fieldSelected ? (
                <FeatureFlag featureName={FEATURE_FLAGS.threeDMap}>
                  <ThreeDControl />
                </FeatureFlag>
              ) : null}
              {filteredLegends.length > 0 && harvest ? (
                <LayerLegend
                  key={TEST_IDS.legendPanel}
                  url={filteredLegends}
                  harvestId={harvest.id}
                  lpVersion={harvest?.stats?.lp_version}
                  lpProcessDateTime={harvest?.stats?.time_reported_utc}
                  crop={harvest?.crop}
                  id={TEST_IDS.legendPanel}
                  swathParameter={selectedSwathParameter}
                  layerInfo={layerInfo}
                />
              ) : null}
              {fieldSelected &&
              !threeD &&
              window.screen.width >= 1000 &&
              window.screen.height >= 800 ? (
                <SwitchSplit />
              ) : null}
            </>
          </MapSourceBoundaryProvider>
        </ErrorBoundary>
      </PageContent>
    </MapProvider>
  )
}
